import { COUNTRY_OPTIONS, countryLabel, DESTINATION_COUNTRY_OPTIONS, TRAVEL_DESTINATION_BLOCKLIST } from "@/app/constants/countries"
import { TravelFormState, TravelMotif } from "@/app/stores/travel/travel-comparator.store"
import { formatDateRange } from "./travelFormat"

export interface TravelMotifOption {
    label: string
    value: TravelMotif
    icon: string
}

export const TRAVEL_MOTIF_OPTIONS: TravelMotifOption[] = [
    { label: "Vacances", value: "vacances", icon: "fa-umbrella-beach" },
    { label: "Affaires", value: "affaires", icon: "fa-briefcase" },
    { label: "Études", value: "etudes", icon: "fa-graduation-cap" },
    { label: "Expatriation", value: "expatriation", icon: "fa-house-chimney" },
    { label: "Working holiday", value: "working_holiday", icon: "fa-earth-europe" },
    { label: "Volontariat", value: "volontariat", icon: "fa-hand-holding-heart" },
]

// Destinations : liste complète moins les pays non assurables (sanctions/conflit).
export const DESTINATION_OPTIONS = DESTINATION_COUNTRY_OPTIONS.filter(
    (option) => !TRAVEL_DESTINATION_BLOCKLIST.includes(option.value)
)
// Résidence : liste complète — une résidence hors Schengen fait remonter les offres visa Schengen.
export const RESIDENCE_OPTIONS = COUNTRY_OPTIONS

// Pays liés à la France par un accord PVT : ailleurs le visa vacances-travail n'existe pas, aucun
// assureur ne propose de produit working holiday (règle miroir de ChapkaAPI côté back).
const WORKING_HOLIDAY_DESTINATIONS = [
    "AR", "AU", "BR", "CA", "CL", "CO", "EC", "HK", "JP", "KR", "MX", "NZ", "PE", "SG", "TW", "UY",
]

export function destinationOptionsFor(motif: TravelMotif) {
    return motif === "working_holiday"
        ? DESTINATION_OPTIONS.filter((option) => WORKING_HOLIDAY_DESTINATIONS.includes(option.value))
        : DESTINATION_OPTIONS
}

export function isDestinationAllowedFor(motif: TravelMotif, code: string): boolean {
    return destinationOptionsFor(motif).some((option) => option.value === code)
}

// Nom complet de la destination (sans l'emoji drapeau), pour les récaps — "AU" → "Australie".
export function destinationLabel(code: string): string {
    return countryLabel(code)
}

export function tripSummary(form: Pick<TravelFormState, "destinationCountries" | "startDate" | "endDate" | "adults">): string {
    const travelers = Math.max(1, form.adults)
    return [
        form.destinationCountries.map(destinationLabel).join(", "),
        formatDateRange(form.startDate, form.endDate),
        `${travelers} voyageur${travelers > 1 ? "s" : ""}`,
    ]
        .filter(Boolean)
        .join(" · ")
}

export function today(): string {
    const date = new Date()
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

// Le porteur du contrat / assuré principal doit être majeur.
export const ADULT_AGE = 18

// Garde-fou de saisie : au-delà, c'est une faute de frappe (une année tapée dans le champ âge),
// pas un voyageur. Les plafonds commerciaux, bien plus bas, sont appliqués par les partenaires.
export const MAX_TRAVELER_AGE = 120

// Âge (années révolues) à la date `at` à partir d'une date de naissance ISO ; null si vide.
export function ageFromIsoBirthdate(iso: string, at: string = today()): number | null {
    if (!iso) {
        return null
    }
    const [year, month, day] = at.split("-").map(Number)
    const [birthYear, birthMonth, birthDay] = iso.split("-").map(Number)
    let age = year - birthYear
    if (month < birthMonth || (month === birthMonth && day < birthDay)) {
        age -= 1
    }
    return age
}

// Date de naissance la plus récente pour être majeur : borne `maxDate` des sélecteurs porteur/assuré principal.
export function maxAdultBirthdate(): string {
    const [year, month, day] = today().split("-").map(Number)
    return `${year - ADULT_AGE}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
}

// The simulation never asks who is a child: companions are qualified by their age
export function travelerLabel(index: number, birthdate: string): string {
    if (index === 0) {
        return "Assuré principal"
    }
    const age = ageFromIsoBirthdate(birthdate)
    return age !== null && age < ADULT_AGE ? "Enfant" : "Accompagnateur adulte"
}
